import { action, computed, makeObservable, observable } from "mobx";
import { CalculateStore } from "../../stores/calculateStore";
import { WidgetApiService } from "./widgetApiService";
import { DefaultRequestStatus, RequestStatus } from "./requestStatus";
import { DefaultEstate } from "../../helpers/estate";
import { AddressClass, AddressModel } from "../../helpers/address";
import { PackageId } from "../../helpers/package";
import { RiskModeId, RiskModel, RisksId } from "../../helpers/risk";
import Formatter, { CapitalizationType } from "../../helpers/formatter";
import { AdditionalQuestionsId, AdditionalQuestionsModel } from "../../helpers/additionalQuestions/additionalQuestions";
import { DefaultPartOfFarm } from "../../helpers/additionalQuestions/partOfFarm";
import { DefaultPaymentMode } from "../../helpers/additionalQuestions/paymentMode";
import {
  DefaultNumberOfDamagesInLastThreeYears
} from "../../helpers/additionalQuestions/numberOfDamagesInLastThreeYears";
import { DefaultBusinessActivityInBuilding } from "../../helpers/additionalQuestions/businessActivity";
import { DefaultClaimlessYearsIn5Years } from "../../helpers/additionalQuestions/claimlessYearsIn5Years";
import { DefaultPolicyDuration } from "../../helpers/additionalQuestions/policyDuration";
import { SelectedValuesDiscount } from "../../helpers/discount";
import { ClientStore } from "../../stores/clientStore";
import { ProposalStore } from "../../stores/proposalStore";
import { ClientLegalGroup, ClientModel, DefaultClient, maxNumberOfInsureds } from "../../helpers/client";
import { getValidPaymentMethod, PaymentMethodTypes } from "../../helpers/paymentMethod";
import { AdditionalConsentsId } from "../../helpers/consents";
import { NnwClient } from "../../helpers/nnwClient";
import { NnwBeneficiary } from "../../helpers/nnwBeneficiary";
import { AnimalProposal } from "../../helpers/catAndDogProposal";
import { SeniorProposal, SeniorProposalModel } from "../../helpers/seniorProposalModel";
import { RisksNetworkTypes } from "../../helpers/risksNetwork";
import { PolicyStore } from "../../stores/policyStore";
import { addDays, isBefore } from "date-fns";
import { PolicyRenewalData } from "../../helpers/policy";
import { AuthorizationRenewalData } from "../../helpers/renewal";
import { UserRole } from "../../helpers/userRole";
import { UserStore } from "../../stores/userStore";
import { PreviousInsurerNameTypes } from "../../helpers/previousInsurerName";
import { ClientContactTypes } from "../../helpers/clientContact";
import { TypeOfActivityId } from "../../helpers/additionalQuestions/typeOfActivity";
import { DictionariesStore } from "../../stores/dictionariesStore";
import Validator, {ValidatedValue} from "../../helpers/validator";
import { ReplenishmentHistoryStore } from "../../stores/replenishmentHistoryStore";
import { UiFlagsStore } from "../../stores/uiFlagsStore";
import CustomEvents from "../../helpers/CustomEvents";

export interface RetrieveCalculationService {
  retrieveCalculationRequestStatus: RequestStatus;
  retrieveCalculationRequest: (calculationId: string) => Promise<void>;
  retrieveContractForRenewalByIdRequestStatus: RequestStatus;
  retrieveContractForRenewalByIdRequest: (contractId: string) => Promise<void>;
  retrieveContractForRenewalBySeriesAndNumberRequestStatus: RequestStatus;
  retrieveContractForRenewalBySeriesAndNumberRequest: (policyRenewalData: PolicyRenewalData) => Promise<void>;
  authorizeRetrieveContractForRenewalRequestStatus: RequestStatus;
  authorizeRetrieveContractForRenewalRequest: (externalId: string, authorizationRenewalData: AuthorizationRenewalData) => Promise<{ authorizedPolicyRenewalOfferId: string }>;
  reset: () => void;
}

export class DefaultRetrieveCalculationService implements RetrieveCalculationService {
  constructor(private widgetApiSrvc: WidgetApiService,
    private calculateStore: CalculateStore,
    private proposalStore: ProposalStore,
    private clientStore: ClientStore,
    private policyStore: PolicyStore,
    private userStore: UserStore,
    private dictionariesStore: DictionariesStore,
    private replenishmentHistoryStore: ReplenishmentHistoryStore,
    private uiFlagsStore: UiFlagsStore
  ) {
    makeObservable(this);
  }

  @observable
  private _retrieveCalculationRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get retrieveCalculationRequestStatus(): RequestStatus {
    return this._retrieveCalculationRequestStatus;
  }

  @action
  public async retrieveCalculationRequest(calculationId: string): Promise<void> {
    const response = await this.retrieveCalculationRequestStatus.handleRequest(
        () => this.widgetApiSrvc.retrieveCalculation(calculationId)
    );
    const policyRenewal = response.calculationRequest.policyRenewal;

    if (policyRenewal) {
      this.calculateStore.setParentCalculationId(calculationId);
      const peselOrRegonValue = response.proposalRequest.clients[0].person.pesel
          ? response.proposalRequest.clients[0].person.pesel
          : response.proposalRequest.clients[0].company.regon;

      const policyRenewalData: PolicyRenewalData = {
        series: policyRenewal.series,
        number: new ValidatedValue<string>(policyRenewal.number),
        peselOrRegon: new ValidatedValue<string>(peselOrRegonValue)
      };

      await this.retrieveContractForRenewalBySeriesAndNumberRequest(policyRenewalData);
      this.updateStores(response, calculationId, true);
    } else {
      this.updateStores(response, calculationId);
      this.uiFlagsStore.setIsOfferEdit(true);
    }
  }

  @observable
  private _retrieveContractForRenewalByIdRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get retrieveContractForRenewalByIdRequestStatus(): RequestStatus {
    return this._retrieveContractForRenewalByIdRequestStatus;
  }

  @action
  public async retrieveContractForRenewalByIdRequest(contractId: string): Promise<void> {
    await this.retrieveContractForRenewalByIdRequestStatus.handleRequest(
      () => this.widgetApiSrvc.retrieveContractForRenewalById(contractId)
    ).then(calculation => this.updateStores(calculation));
  }

  @observable
  private _retrieveContractForRenewalBySeriesAndNumberRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get retrieveContractForRenewalBySeriesAndNumberRequestStatus(): RequestStatus {
    return this._retrieveContractForRenewalBySeriesAndNumberRequestStatus;
  }

  @action
  public async retrieveContractForRenewalBySeriesAndNumberRequest(policyRenewalData: PolicyRenewalData): Promise<void> {
    await this.retrieveContractForRenewalBySeriesAndNumberRequestStatus.handleRequest(
      () => this.widgetApiSrvc.retrieveContractForRenewalBySeriesAndNumber(policyRenewalData)
    ).then(calculation => {
      if (calculation) {
        this.updateStores(calculation);
        this.reloadApk();
      }
    });
  }

  @observable
  private _authorizeRetrieveContractForRenewalRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get authorizeRetrieveContractForRenewalRequestStatus(): RequestStatus {
    return this._authorizeRetrieveContractForRenewalRequestStatus;
  }

  @action
  public async authorizeRetrieveContractForRenewalRequest(externalId: string, authorizationRenewalData: AuthorizationRenewalData) {
    return await this.authorizeRetrieveContractForRenewalRequestStatus.handleRequest(
      () => this.widgetApiSrvc.authorizeRetrieveContractForRenewal(externalId, authorizationRenewalData)
    );
  }

  @action
  public reset() {
    this._retrieveCalculationRequestStatus = new DefaultRequestStatus();
    this._retrieveContractForRenewalByIdRequestStatus = new DefaultRequestStatus();
    this._retrieveContractForRenewalBySeriesAndNumberRequestStatus = new DefaultRequestStatus();
    this._authorizeRetrieveContractForRenewalRequestStatus = new DefaultRequestStatus();
  }

  private updateStores(retrievedData: any, calculationId?: string, skipClaimlessYearsUpdate?: boolean) {
    if (!retrievedData) {
      return;
    }
    this.updateCalculationId(calculationId);

    const calculationRequest = retrievedData.calculationRequest;
    const proposalRequest = retrievedData.proposalRequest;


    if (calculationRequest) {
      this.updatePolicySeriesAndNumber(calculationRequest.policyRenewal);
      this.updateAdditionalInsurances(calculationRequest.insurance);
      this.updateApk(calculationRequest.apk);
      this.updateEstate(calculationRequest.estate);
      this.updateClientFromCalculation(retrievedData);
      this.updateSelectedPackage(calculationRequest.selectedPackage, calculationRequest.insurance);
      this.updatePackages(calculationRequest);
      this.updateAdditionalQuestions(calculationRequest, skipClaimlessYearsUpdate);
      this.updateDiscountsAndAdditionalClauses(calculationRequest.insurance);
      this.updateInitialFloodState(calculationRequest.insurance.flood);
    }

    if (proposalRequest) {
      this.updateNnw(proposalRequest.insurance);
      this.updateGravestone(proposalRequest.insurance);
      this.updateSafeSenior(proposalRequest);
      this.updateCession(proposalRequest.cession);
      this.updateClientFromProposal(retrievedData);
      this.updatePaymentMethod(proposalRequest.paymentMethod);
      this.updateIssuePlace(proposalRequest.issuePlace);
      this.updateConsents(proposalRequest.consents);
      this.updateCatAndDog(proposalRequest.insurance);
      this.updateDistanceContract(proposalRequest.distanceContract);
      this.updateEurofinanceFields(proposalRequest);
      this.updateInsuredInSecondOpinion(proposalRequest.insurance);
    }

    this.moveUnexpectedInsuredsToAdditionalClauses();
  }

  private updateDistanceContract(distanceContract: any) {
    this.proposalStore.distancePolicy.value = UserRole.isDistancePolicyRole(this.userStore.roles) ? !!distanceContract : false;
  }

  private updateCatAndDog(insurance: any) {
    if (!this.calculateStore.additionalInsurances.catAndDog.checked) {
      return;
    }

    this.proposalStore.catAndDog.reset();
    if (insurance?.catAndDog?.animals) {
      for (const animal of insurance.catAndDog?.animals) {
        this.proposalStore.catAndDog.animals.push(new AnimalProposal(animal));
      }
    }

    const animalsCalculationProposalCountDifference = this.getAnimalsCountDifference();
    for (let i = 0; i < animalsCalculationProposalCountDifference; i++) {
      this.proposalStore.catAndDog.animals.push(new AnimalProposal());
    }
  }

  private getAnimalsCountDifference() {
    const calculationAnimalsCount = this.calculateStore.additionalInsurances.catAndDog.catAndDogCount();
    const proposalAnimalsCount = (this.proposalStore.catAndDog.animals)
      ? this.proposalStore.catAndDog.animals.length
      : 0;

    const animalsCountDifference = calculationAnimalsCount - proposalAnimalsCount;
    return (animalsCountDifference >= 0) ? animalsCountDifference : 0;
  }

  private updateNnw(insurance: any) {
    if (!this.calculateStore.additionalInsurances.nnwType.checked) {
      return;
    }

    this.proposalStore.nnw.reset();
    if (insurance?.nnw?.clients) {
      for (const client of insurance.nnw.clients) {
        this.proposalStore.nnw.clients.push(this.mapNnwClient(client));
      }
    }

    const nnwCalculationProposalCountDifference = this.getNnwCountDifference();
    for (let i = 0; i < nnwCalculationProposalCountDifference; i++) {
      this.proposalStore.nnw.clients.push(new NnwClient());
    }
  }

  private getNnwCountDifference() {
    const calculationNnwClientsCount = Number(this.calculateStore.additionalInsurances.nnwType.numberOfClients);
    const proposalNnwClientsCount = this.proposalStore.nnw.clients.length;
    const nnwClientsCountDifference = calculationNnwClientsCount - proposalNnwClientsCount;
    return (nnwClientsCountDifference >= 0) ? nnwClientsCountDifference : 0;
  }

  private mapNnwClient(client: any) {
    const nnwClient = new NnwClient(client);
    const firstName = client.fullName?.substr(0, client.fullName?.indexOf(" "))?.trim();
    const lastName = client.fullName?.substr(client.fullName?.indexOf(" ") + 1)?.trim();
    nnwClient.firstName.value = Formatter.capitalizeWords(firstName, CapitalizationType.full);
    nnwClient.lastName.value = Formatter.capitalizeWords(lastName, CapitalizationType.full);
    nnwClient.heartAttackAndStroke = client.heartAttackAndStrokeInsurance;
    nnwClient.addressTheSame = (this.calculateStore.estate.address as AddressClass).isEqualTo(new AddressClass(client.address));

    if (nnwClient.addressTheSame) {
      nnwClient.address = AddressClass.default();
    } else {
      nnwClient.address = new AddressClass(client.address);
    }

    nnwClient.beneficiaryChecked = !!client.beneficiary;

    if (nnwClient.beneficiaryChecked) {
      nnwClient.beneficiary = new NnwBeneficiary(client.beneficiary);
    }

    return nnwClient;
  }

  private updateGravestone(insurance: any) {
    if (!insurance) {
      return;
    }
    const gravestone = insurance.gravestone;
    if (!gravestone) {
      return;
    }

    this.proposalStore.gravestone.cemeteryAddress.value = Formatter.capitalizeWords(gravestone.cemeteryAddress?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.gravestone.cemeteryName.value = Formatter.capitalizeWords(gravestone.cemeteryName?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.gravestone.plot = Formatter.capitalizeWords(gravestone.plot?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.gravestone.row.value = gravestone.row?.trim() ?? "";
    this.proposalStore.gravestone.sector.value = Formatter.capitalizeWords(gravestone.sector?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.gravestone.year.value = gravestone.year ?? "";
  }

  private updateCession(cession: any) {
    if (!cession) {
      return;
    }

    this.proposalStore.cession.cessionSelected = true;
    this.proposalStore.cession.bankName.value = cession.name?.trim() ?? "";
    this.proposalStore.cession.bankNameContinuation.value = Formatter.capitalizeWords(cession.nameContinuation?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.cession.address.zipCode.value = cession.zipCode?.trim() ?? "";
    this.proposalStore.cession.address.city.value = Formatter.capitalizeWords(cession.city?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.cession.address.county.value = Formatter.capitalizeWords(cession.county?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.cession.address.street.value = Formatter.capitalizeWords(cession.street?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.cession.address.buildingNumber.value = Formatter.capitalizeWords(cession.buildingNumber?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.cession.address.apartmentNumber.value = Formatter.capitalizeWords(cession.apartmentNumber?.trim(), CapitalizationType.full) ?? "";
    this.proposalStore.cession.nip.value = cession.nip?.trim() ?? "";
    this.proposalStore.cession.regon.value = cession.regon?.trim() ?? "";
  }

  private updateCalculationId(calculationId: string | undefined) {
    if (calculationId) {
      this.calculateStore.setParentCalculationId(calculationId);
    }
  }

  private updateApk(apkResponse: any) {
    const notExternalApk = apkResponse ? Object.values(apkResponse).some(value => value === true || value === false) : false;
    //only house = external_no / clientConsent_no
    const onlyHouse = apkResponse?.onlyHouse === true &&
      Object.keys(apkResponse).every(key => key === 'onlyHouse' || apkResponse[key] === null);
    if (notExternalApk) {
      if (onlyHouse) {
        this.calculateStore.setApkExternalNoClientConsentNo();
      }
      else {
        this.calculateStore.setApkUsingOldModel(apkResponse, this.uiFlagsStore.isDirect);
      }

    } else if (this.isOnlyNnw()) {
      this.calculateStore.setApkOnlyNnw(this.uiFlagsStore.isDirect);
    } else {
      this.calculateStore.setApkExternal(this.uiFlagsStore.isDirect);

    }

  }

  private updateEstate(retrievedEstate: any) {
    if (!retrievedEstate) {
      return;
    }

    const address = retrievedEstate.address ?
      new AddressClass(retrievedEstate.address) :
      new AddressClass();

    const estate = new DefaultEstate({
      address,
      area: retrievedEstate.area,
      constructionYear: retrievedEstate.constructionYear,
      flammableBuildingStructure: retrievedEstate.flammableBuildingStructure,
      floor: retrievedEstate.floor,
      overhaulYear: retrievedEstate.overhaulYear,
      ownershipType: retrievedEstate.ownershipType,
      propertyType: retrievedEstate.propertyType
    });

    this.calculateStore.setEstate(estate);
    this.replenishmentHistoryStore.setEstate(estate);
  }

  private updateSelectedPackage(selectedPackage: string, insurance: any) {
    this.calculateStore.resetPackages();

    if (!selectedPackage) {
      if (insurance && insurance.riskMode && insurance.riskMode === RiskModeId.allRisk) {
        this.calculateStore.selectPackage(PackageId.maxi);
      }

      return;
    }

    selectedPackage = selectedPackage.toLowerCase();

    if (PackageId.hasOwnProperty(selectedPackage)) {
      this.calculateStore.selectPackage(selectedPackage as PackageId);
    }
  }

  private updatePackages(calculation: any) {
    if (!calculation.insurance) {
      return;
    }

    this.calculateStore.packages.forEach((packageData) => {
      const packageId = packageData.id;
      const selectPackage = this.calculateStore.selectedPackage;
      if (selectPackage === packageId) {
        this.calculateStore.packages.get(packageId)?.risks?.forEach((risk: RiskModel) => {
          if (risk.getInsuranceSumFromRetrievedCalculation) {
            const insuranceSum = risk.getInsuranceSumFromRetrievedCalculation(calculation);
            if (insuranceSum || insuranceSum === 0) {
              if (insuranceSum) {
                risk.insuranceSum = Formatter.convertCentsToNormalNumber(insuranceSum);
              }

              if (risk.risksNetworkValue !== RisksNetworkTypes.notAvailable) {
                risk.available = true;
              }
            } else {
              risk.insuranceSum = undefined;

              if (risk.risksNetworkValue !== RisksNetworkTypes.required) {
                risk.available = false;
              }
            }
          } else {
            if (risk.getAvailableFromRetrievedCalculation) {
              risk.available = risk.getAvailableFromRetrievedCalculation(calculation);
            }
          }
        });
      }
    });

    if (this.isOnlyMovables(calculation)) {
      this.checkOnlyFixedElementsInMiniAndMaxiPackage();
    }
  }

  private checkOnlyFixedElementsInMiniAndMaxiPackage() {
    this.calculateStore.packages.get(PackageId.mini)!.risks.get(RisksId.onlyFixedElements)!.available = true;
    this.calculateStore.packages.get(PackageId.maxi)!.risks.get(RisksId.onlyFixedElements)!.available = true;
  }

  private isOnlyMovables(calculationReqeust: any) {
    const movables = calculationReqeust.insurance.movables;
    const onlyFixedElements = calculationReqeust.insurance.onlyFixedElements;
    const detachedHouseOrFlatWithFixedElements = calculationReqeust.insurance.detachedHouseOrFlatWithFixedElements;
    return !!movables && !onlyFixedElements && !detachedHouseOrFlatWithFixedElements;
  }

  private updateAdditionalInsurances(retrievedInsurance: any) {
    if (!retrievedInsurance) {
      return;
    }

    for (const additionalInsurancesKey in this.calculateStore.additionalInsurances) {
      if (this.calculateStore.additionalInsurances.hasOwnProperty(additionalInsurancesKey)) {
        // @ts-ignore
        const additionalInsurance = this.calculateStore.additionalInsurances[additionalInsurancesKey];

        if (additionalInsurance.getValuesFromRetrievedCalculation) {
          additionalInsurance.getValuesFromRetrievedCalculation(retrievedInsurance, this.calculateStore);
        }
      }
    }
  }

  private updateAdditionalQuestions(retrievedCalculation: any, skipClaimlessYearsUpdate:boolean = false) {
    if (!retrievedCalculation) {
      return;
    }

    const isRenewalWithInsuranceHistory = (retrievedCalculation.policyRenewal && retrievedCalculation.policyRenewal?.series !== "WDM")
      && (!!retrievedCalculation.courseOfInsurance?.claimlessYearsIn5Years && !!retrievedCalculation.courseOfInsurance?.numberOfDamagesInLastThreeYears);

    const retrievedInsurance = retrievedCalculation.insurance;
    const retrievedEstate = retrievedCalculation?.estate;
    const retrievedAdditionalProtections = retrievedEstate?.additionalProtections;
    const additionalQuestions: AdditionalQuestionsModel = {
      [AdditionalQuestionsId.businessActivityInBuilding]: new DefaultBusinessActivityInBuilding(retrievedEstate?.businessActivity),
      [AdditionalQuestionsId.typeOfActivity]: retrievedEstate?.businessActivityType ?? TypeOfActivityId.registered,
      [AdditionalQuestionsId.partOfFarm]: new DefaultPartOfFarm(retrievedEstate?.partOfFarm ?? undefined),
      [AdditionalQuestionsId.additionalProtections]: {
        antiburglarDoors: retrievedAdditionalProtections?.antiburglarDoors,
        caretaker: retrievedAdditionalProtections?.caretaker,
        antiburglarInstallation: retrievedAdditionalProtections?.antiburglarInstallation,
        firefightingInstallation: retrievedAdditionalProtections?.firefightingInstallation,
        fireExtinguishing: retrievedAdditionalProtections?.fireExtinguishing,
        securityWindows: retrievedAdditionalProtections?.securityWindows
      },
      [AdditionalQuestionsId.policyDuration]: new DefaultPolicyDuration(retrievedInsurance.insuranceDuration),
      [AdditionalQuestionsId.paymentMode]: new DefaultPaymentMode(retrievedInsurance.paymentMode),
      [AdditionalQuestionsId.startDate]: this.updateStartDate(retrievedInsurance.startDate)
    };

    if (!skipClaimlessYearsUpdate) {
      additionalQuestions[AdditionalQuestionsId.claimlessYearsIn5Years] = new DefaultClaimlessYearsIn5Years(
          retrievedCalculation.courseOfInsurance?.claimlessYearsIn5Years,
          !isRenewalWithInsuranceHistory
      );

      additionalQuestions[AdditionalQuestionsId.numberOfDamagesInLastThreeYears] = new DefaultNumberOfDamagesInLastThreeYears(
          retrievedCalculation.courseOfInsurance?.numberOfDamagesInLastThreeYears,
          !isRenewalWithInsuranceHistory
      );
    }

    this.calculateStore.setAdditionalQuestions(additionalQuestions);
  }

  private updateStartDate(retrievedStartDate: any) {
    const minimumStartDate = this.policyStore.isPolicyRenewal ? new Date() : addDays(Date.now(), 1);
    return (!retrievedStartDate || isBefore(new Date(retrievedStartDate), Date.now()))
      ? minimumStartDate
      : new Date(retrievedStartDate);
  }

  private updateDiscountsAndAdditionalClauses(retrievedInsurance: any) {
    if (!retrievedInsurance) {
      return;
    }
    const discounts = new SelectedValuesDiscount({
      agentDiscount: retrievedInsurance.corrections.agentDiscount,
      managerDiscount: retrievedInsurance.corrections.managerDiscountInPercent,
      durManagerDiscount: retrievedInsurance.corrections.managerDiscountInPercent,
      individualRiskAssessment: retrievedInsurance.corrections.individualRiskAssessmentRaiseInPercent,
      employeeDiscount: retrievedInsurance.corrections.employeeDiscount,
      specialDiscount: retrievedInsurance.corrections.specialDiscountInPercent,
      dedicatedDiscountCode: retrievedInsurance.corrections.dedicatedDiscountCode?.trim(),
      nnwDiscount: retrievedInsurance.corrections.nnwDiscountInPercent,
      otherClaimlessPolicyDiscount: retrievedInsurance.corrections.otherClaimlessPolicy
    });
    const additionalClauses = Formatter.capitalizeWords(retrievedInsurance.additionalClauses?.trim(), CapitalizationType.full);
    this.calculateStore.setDiscountsAndAdditionalClauses(discounts, additionalClauses ?? "");
  }


  private updateClientFromCalculation(retrievedData: any) {
    const retrievedClients = retrievedData.calculationRequest?.clients;
    const clients: ClientModel[] = [];

    retrievedClients?.forEach((retrievedClient: any) => {
      clients.push(new DefaultClient({
        id: retrievedClient.insurer ? 0 : undefined,
        legalGroup: retrievedClient.legalGroup,
        insurer: retrievedClient.insurer,
        insured: retrievedClient.insured
      }));
    });

    this.clientStore.setClients(clients);
  }

  private updateClientFromProposal(retrievedData: any) {
    const retrievedProposal = retrievedData.proposalRequest;
    const retrievedClients = retrievedProposal?.clients;
    const retrievedEstate = retrievedData.calculationRequest?.estate;
    const clients = this.clientStore.clients;
    const updatedClients: ClientModel[] = [];

    retrievedClients?.forEach((retrievedClient: any, index: number) => {
      const personAddress = new AddressClass(retrievedClient.person?.personAddress);
      const companyAddress = new AddressClass(retrievedClient.company?.companyAddress);
      const contactAddress = new AddressClass(retrievedClient.contactAddress);
      const estateAddress = new AddressClass(retrievedEstate?.address);
      const personAddressTheSame = personAddress.isEqualTo(estateAddress);
      const companyAddressTheSame = companyAddress.isEqualTo(estateAddress);
      const contactAddressTheSame = contactAddress.isEqualTo(clients[index].legalGroup === ClientLegalGroup.PERSON ? personAddress : companyAddress);

      updatedClients.push(new DefaultClient({
        id: clients[index].id,
        insurer: clients[index].insurer,
        insured: clients[index].insured,
        legalForm: retrievedClient.legalForm,
        legalGroup: clients[index].legalGroup,
        pesel: this.getPesel(retrievedClient.person?.pesel?.trim()),
        firstName: retrievedClient.person?.firstName?.trim(),
        lastName: retrievedClient.person?.lastName?.trim(),
        regon: retrievedClient.company?.regon?.trim(),
        nip: retrievedClient.company?.nip?.trim(),
        companyName: retrievedClient.company?.name?.trim(),
        email: retrievedProposal?.insurerEmail?.trim(),
        phone: this.getPhoneNumber(retrievedProposal),
        personAddress: personAddressTheSame ? AddressClass.default() : personAddress,
        companyAddress: companyAddressTheSame ? AddressClass.default() : companyAddress,
        contactAddress: contactAddressTheSame ? AddressClass.default() : contactAddress,
        personAddressTheSame,
        companyAddressTheSame,
        contactAddressTheSame
      }));
    });

    this.clientStore.setClients(updatedClients);
  }

  private getPesel(pesel: string | undefined) {
    return Validator.pesel(pesel)
      ? pesel
      : "";
  }

  private getPhoneNumber(retrievedProposal: any) {
    const phone = retrievedProposal?.insurerPhoneNumber;
    return Formatter.removeNonPhoneCharacters(phone).trim();
  }

  private updatePaymentMethod(paymentMethod: string) {
    const allowedMethods = this.dictionariesStore.paymentMethods;
    this.proposalStore.setPaymentMethod(getValidPaymentMethod(paymentMethod as PaymentMethodTypes | undefined, allowedMethods));
  }

  private updateIssuePlace(issuePlace: string | undefined) {
    this.proposalStore.setIssuePlace(Formatter.capitalizeWords(issuePlace?.trim(), CapitalizationType.full) ?? "");
  }

  private updateConsents(consents: any) {
    this.proposalStore.setConsent(AdditionalConsentsId.dataProcessingForMarketing, !!consents?.dataProcessingForMarketing);
    this.proposalStore.setConsent(AdditionalConsentsId.thirdPartyMarketing, !!consents?.thirdPartyMarketing);
    this.proposalStore.setConsent(AdditionalConsentsId.electronicMeansMarketing, !!consents?.electronicMeansMarketing);
    this.proposalStore.setConsent(AdditionalConsentsId.phoneCallMarketing, !!consents?.phoneCallMarketing);
  }

  private updateSafeSenior(retrievedProposal: any) {
    if (!this.calculateStore.additionalInsurances.safeSenior.checked) {
      return;
    }
    const retrievedSeniors = retrievedProposal?.insurance?.safeSenior?.clients;
    const seniors: SeniorProposalModel[] = [];

    if (retrievedSeniors) {
      retrievedSeniors.forEach((senior: any) => {
        seniors.push(new SeniorProposal({
          firstName: senior.firstName?.trim(),
          lastName: senior.lastName?.trim(),
          address: new AddressClass(senior.address),
          pesel: senior.pesel?.trim()
        }));
      });
    }

    const seniorsCalculationProposalCountDifference = this.getSeniorsCountDifference(seniors);
    for (let i = 0; i < seniorsCalculationProposalCountDifference; i++) {
      seniors.push(new SeniorProposal());
    }

    this.proposalStore.setSafeSenior(seniors);
  }

  private getSeniorsCountDifference(seniors: SeniorProposalModel[]) {
    const calculationSeniorCount = Number(this.calculateStore.additionalInsurances.safeSenior.safeSeniorCount);
    const proposalSeniorCount = seniors.length;

    const seniorsCountDifference = calculationSeniorCount - proposalSeniorCount;
    return (seniorsCountDifference >= 0) ? seniorsCountDifference : 0;
  }

  private updatePolicySeriesAndNumber(policyRenewal: any) {
    if (!policyRenewal) {
      return;
    }

    this.policyStore!.setSeries(policyRenewal.series);
    this.policyStore!.setNumber(policyRenewal.number);
    this.policyStore!.setPeselOrRegon(policyRenewal.peselOrRegon);
  }

  private updateEurofinanceFields(retrievedProposal: any) {
    if (UserRole.isEurofinanceRole(this.userStore.roles)) {
      this.proposalStore.setAuthorisedOfwca(retrievedProposal.authorizedOfwcaEf);
      this.proposalStore.setClientContact(retrievedProposal.clientContactType ?? ClientContactTypes.COMPANY);
      this.setPreviousInsurerName(retrievedProposal.previousInsurerName);
    }
  }

  private updateInsuredInSecondOpinion(insurance: any) {
    if (!this.calculateStore.additionalInsurances.secondOpinion.checked) {
      return;
    }

    this.clientStore.setInsuredInSecondOpinion(insurance.secondOpinion.pesel);
  }

  private isOnlyNnw(): boolean {
    const nnw = this.calculateStore.additionalInsurances.nnwType;

    if (!nnw.checked) {
      return false;
    }

    for (const additionalInsurancesKey in this.calculateStore.additionalInsurances) {
      if (this.calculateStore.additionalInsurances.hasOwnProperty(additionalInsurancesKey)) {
        // @ts-ignore
        const additionalInsurance = this.calculateStore.additionalInsurances[additionalInsurancesKey];

        if (additionalInsurance.checked && !this.isNnwRisk(additionalInsurancesKey as RisksId)) {
          return false;
        }
      }
    }

    return true;
  }

  private isNnwRisk(riskId: RisksId): boolean {
    switch (riskId) {
      case RisksId.nnwWithoutProperty:
      case RisksId.nnwType:
      case RisksId.nnwSenior:
      case RisksId.nnwHeartAttackAndStroke:
        return true;
      default:
        return false;
    }
  }

  private setPreviousInsurerName(previousInsurerName: any) {
    if (this.policyStore.isPolicyRenewal) {
      this.proposalStore.setPreviousInsurerName(PreviousInsurerNameTypes.TUZ_TUW);
    } else {
      this.proposalStore.setPreviousInsurerName(previousInsurerName);
    }
  }

  private moveUnexpectedInsuredsToAdditionalClauses() {
    if (this.clientStore.clients.length <= maxNumberOfInsureds) {
      return;
    }

    function addressToDisplay(address: AddressModel, addressName: string) {
      const streetToDisplay = address.street.value ? `, ul. ${address.street.value}` : "";
      const apartmentNumberToDisplay = address.apartmentNumber.value ? `/${address.apartmentNumber.value}` : "";

      return address.zipCode.value && address.city.value ? `${addressName} ${address.zipCode.value} ${address.city.value}${streetToDisplay} ${address.buildingNumber.value}${apartmentNumberToDisplay}` : "";
    }

    function clientDataToDisplay(client: ClientModel) {
      const peselToDisplay = client.pesel?.value ? `, PESEL: ${client.pesel?.value}` : "";
      const regonToDisplay = client.regon?.value ? `, REGON: ${client.regon?.value}` : "";
      const nipToDisplay = client.nip?.value ? `, NIP: ${client.nip?.value}` : "";
      let companyNameToDisplay = (client.companyName?.value && (client.firstName?.value || client.lastName?.value)) ? `, ` : "";
      companyNameToDisplay += client.companyName?.value ? `Nazwa firmy: ${client.companyName?.value}` : "";
      const firstNameToDisplay = client.firstName?.value ? `${client.firstName?.value}` : "";
      const lastNameToDisplay = client.lastName?.value ? ` ${client.lastName?.value}` : "";

      return `${firstNameToDisplay}${lastNameToDisplay}${companyNameToDisplay}${peselToDisplay}${regonToDisplay}${nipToDisplay}`;
    }

    let additionalClauses = "Dodatkowi ubezpieczeni:";
    for (let i = maxNumberOfInsureds; i < this.clientStore.clients.length; i++) {
      const client = this.clientStore.clients[i];
      const isPerson = client.legalGroup === ClientLegalGroup.PERSON;
      const personAddress = client.personAddressTheSame ? this.calculateStore.estate.address : client.personAddress;
      const companyAddress = client.companyAddressTheSame ? this.calculateStore.estate.address : client.companyAddress;
      const address = isPerson ? personAddress : companyAddress;
      const contactAddress = client.contactAddressTheSame ? address : client.contactAddress;

      const clientAddressToDisplay = addressToDisplay(address, isPerson ? "Adres zamieszkania:" : "Adres siedziby firmy:");
      const contactAddressToDisplay = addressToDisplay(contactAddress, "Adres korespondencyjny:");
      additionalClauses += `\n${clientDataToDisplay(client)}\n${clientAddressToDisplay}\n${contactAddressToDisplay}\n`;
    }
    this.clientStore.clients.splice(maxNumberOfInsureds, this.clientStore.clients.length);

    this.calculateStore.setAdditionalClauses((this.calculateStore.additionalClauses ? this.calculateStore.additionalClauses + "\n" : "") + additionalClauses.trim());
  }

  reloadApk() {
    const apkConfig = {
      tariffId: 40,
      sourceType: this.uiFlagsStore.partnerName.toUpperCase(),
      apkUuid: '',
      readonly: false,
      hideSubmitButton: false,
      selectedAnswers: this.calculateStore.getApkAnswers,
    };

    const apkConfigEvent = new CustomEvent(CustomEvents.ApkPassUpdatedConfig, {
      detail: apkConfig
    });

    window.dispatchEvent(apkConfigEvent);
  }

  private updateInitialFloodState(floodState: boolean) {
    this.calculateStore.setInitialFloodState(floodState);
  }
}
